import { AirtableError, AirtableErrorType, ErrorSeverity } from './types';

console.log('[errors.ts loaded - start]');

/**
 * Airtable Error Handling Module
 * 
 * Provides comprehensive error handling for Airtable API interactions
 * Includes custom error classes, error codes, and logging utilities
 */

/**
 * Error for Airtable API responses with non-200 status codes
 */
export class AirtableApiError extends AirtableError {
  status: number;
  statusText: string;
  data: any;
  
  constructor(status: number, statusText: string, data: any, context?: Record<string, any>) {
    // Determine error type based on status code
    let type = AirtableErrorType.UNKNOWN;
    let severity = ErrorSeverity.ERROR;
    
    if (status === 401 || status === 403) {
      type = AirtableErrorType.AUTHENTICATION;
      severity = ErrorSeverity.CRITICAL;
    } else if (status === 404) {
      type = AirtableErrorType.NOT_FOUND;
      severity = ErrorSeverity.WARNING;
    } else if (status === 422) {
      type = AirtableErrorType.VALIDATION;
      severity = ErrorSeverity.WARNING;
    } else if (status === 429) {
      type = AirtableErrorType.RATE_LIMIT;
      severity = ErrorSeverity.WARNING;
    } else if (status >= 500) {
      type = AirtableErrorType.SERVER;
      severity = ErrorSeverity.ERROR;
    } else if (status >= 400) {
      type = AirtableErrorType.CLIENT;
      severity = ErrorSeverity.WARNING;
    }
    
    super(`Airtable API error: ${status} ${statusText}`, type, severity, undefined, context);
    
    this.name = 'AirtableApiError';
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
  
  /**
   * Get human-readable error message based on Airtable error structure
   */
  getDetailedMessage(): string {
    if (this.data?.error?.message) {
      return `${this.message}: ${this.data.error.message}`;
    }
    
    if (this.data?.error?.type) {
      return `${this.message}: ${this.data.error.type}`;
    }
    
    return this.message;
  }
  
  /**
   * Check if the error is a rate limit error
   */
  isRateLimitError(): boolean {
    return this.status === 429 || this.type === AirtableErrorType.RATE_LIMIT;
  }
  
  /**
   * Get retry-after value from rate limit response if available
   */
  getRetryAfter(): number | null {
    if (this.data?.error?.retryAfter || this.data?.retryAfter) {
      return parseInt(this.data?.error?.retryAfter || this.data?.retryAfter, 10);
    }
    
    return null;
  }
}

/**
 * Error for network failures connecting to Airtable
 */
export class AirtableNetworkError extends AirtableError {
  constructor(message: string, originalError?: Error | unknown, context?: Record<string, any>) {
    super(
      message || 'Network error connecting to Airtable API', 
      AirtableErrorType.NETWORK,
      ErrorSeverity.ERROR,
      originalError,
      context
    );
    this.name = 'AirtableNetworkError';
  }
}

/**
 * Error for validation issues in API requests or responses
 */
export class AirtableValidationError extends AirtableError {
  validationErrors: string[];
  
  constructor(message: string, validationErrors: string[] = [], context?: Record<string, any>) {
    super(
      message || 'Validation error in Airtable request or response', 
      AirtableErrorType.VALIDATION,
      ErrorSeverity.WARNING,
      undefined,
      context
    );
    this.name = 'AirtableValidationError';
    this.validationErrors = validationErrors;
  }
}

/**
 * Error for rate limiting issues
 */
export class AirtableRateLimitError extends AirtableError {
  retryAfter?: number;
  
  constructor(message: string, retryAfter?: number, context?: Record<string, any>) {
    super(
      message || 'Airtable API rate limit exceeded', 
      AirtableErrorType.RATE_LIMIT,
      ErrorSeverity.WARNING,
      undefined,
      context
    );
    this.name = 'AirtableRateLimitError';
    this.retryAfter = retryAfter;
  }
}

/**
 * Error for caching failures
 */
export class AirtableCacheError extends AirtableError {
  constructor(message: string, originalError?: Error | unknown, context?: Record<string, any>) {
    super(
      message || 'Error in Airtable cache operations', 
      AirtableErrorType.CACHING,
      ErrorSeverity.WARNING,
      originalError,
      context
    );
    this.name = 'AirtableCacheError';
  }
}

/**
 * Error for authentication issues
 */
export class AirtableAuthError extends AirtableError {
  constructor(message: string, context?: Record<string, any>) {
    super(
      message || 'Airtable authentication error', 
      AirtableErrorType.AUTHENTICATION,
      ErrorSeverity.CRITICAL,
      undefined,
      context
    );
    this.name = 'AirtableAuthError';
  }
}

/**
 * Centralized error logging for Airtable errors
 * @param error The error to log
 * @param additionalContext Any additional context to include
 */
export function logAirtableError(error: Error | AirtableError, additionalContext?: Record<string, any>): void {
  console.log('[logAirtableError] called with:', error, additionalContext, 'stack:', new Error().stack);
  if (!error) {
    console.log('[logAirtableError] Called with undefined error:', error, additionalContext, 'stack:', new Error().stack);
    return;
  }
  try {
    // Default error details
    let errorDetails: Record<string, any> = {
      message: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString(),
    };
    
    // Add Airtable-specific error details if available
    if (error instanceof AirtableError) {
      errorDetails = {
        ...errorDetails,
        ...error.toJSON(),
        ...(additionalContext || {})
      };
    } else {
      errorDetails.name = error.name;
      errorDetails.context = additionalContext;
    }
    
    // Determine log level based on severity
    const logLevel = error instanceof AirtableError && error.severity === ErrorSeverity.CRITICAL ? 'error' :
                    error instanceof AirtableError && error.severity === ErrorSeverity.WARNING ? 'warn' : 'error';
    
    // Log the error with appropriate level
    switch (logLevel) {
      case 'warn':
        console.warn('Airtable Warning:', errorDetails);
        break;
      case 'error':
      default:
        console.error('Airtable Error:', errorDetails);
        break;
    }
    
    // TODO: Add integration with monitoring services like Sentry
    // if (process.env.NODE_ENV === 'production') {
    //   captureException(error, { extra: errorDetails });
    // }
  } catch (err) {
    console.log('[logAirtableError] Exception during logging:', err, error, additionalContext, 'stack:', new Error().stack);
  }
}

/**
 * Check if an error is a specific Airtable error type
 */
export function isAirtableError(error: unknown): error is AirtableError {
  console.log('[isAirtableError] called with:', error, 'stack:', new Error().stack);
  if (!error) {
    console.log('[isAirtableError] Called with undefined error:', error, 'stack:', new Error().stack);
    return false;
  }
  return error instanceof AirtableError;
}

/**
 * Wrap a generic error as an AirtableError with appropriate context
 */
export function wrapError(error: unknown, context?: Record<string, any>): AirtableError {
  if (isAirtableError(error)) {
    // If it's already an AirtableError, just add context if provided
    if (context) {
      error.context = { ...(error.context || {}), ...context };
    }
    return error;
  }
  
  // Handle fetch/network errors specifically
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return new AirtableNetworkError('Network error connecting to Airtable API', error, context);
  }
  
  // Generic error wrapping
  return new AirtableError(
    error instanceof Error ? error.message : String(error),
    AirtableErrorType.UNKNOWN, 
    ErrorSeverity.ERROR,
    error,
    context
  );
}

/**
 * Create appropriate error message for user display
 */
export function getUserFriendlyMessage(error: unknown): string {
  console.log('[getUserFriendlyMessage] called with:', error, 'stack:', new Error().stack);
  if (!error) {
    console.log('[getUserFriendlyMessage] Called with undefined error:', error, 'stack:', new Error().stack);
    return 'Unknown error';
  }
  if (error instanceof AirtableApiError) {
    switch (error.type) {
      case AirtableErrorType.AUTHENTICATION:
        return 'Unable to authenticate with Airtable. Please check your API credentials.';
      case AirtableErrorType.RATE_LIMIT:
        return 'Airtable rate limit exceeded. Please try again in a few moments.';
      case AirtableErrorType.PERMISSION:
        return 'You do not have permission to perform this operation in Airtable.';
      case AirtableErrorType.NOT_FOUND:
        return 'The requested record or table could not be found in Airtable.';
      case AirtableErrorType.VALIDATION:
        return 'The data provided is invalid for this Airtable operation.';
      case AirtableErrorType.SERVER:
        return 'Airtable is experiencing issues. Please try again later.';
      default:
        return 'An error occurred while communicating with Airtable.';
    }
  } else if (error instanceof AirtableNetworkError) {
    return 'Unable to connect to Airtable. Please check your internet connection.';
  } else if (error instanceof AirtableValidationError) {
    return `Validation error: ${error.validationErrors.join(', ')}`;
  } else if (error instanceof AirtableRateLimitError) {
    return 'Too many requests to Airtable. Please try again in a few moments.';
  } else if (error instanceof AirtableCacheError) {
    return 'An error occurred with data caching. Please refresh the page.';
  } else if (error instanceof AirtableAuthError) {
    return 'Authentication error with Airtable. Please check your credentials or log in again.';
  } else if (error instanceof AirtableError) {
    return 'An error occurred with the Airtable integration.';
  }
  
  // Generic error message
  return 'An unexpected error occurred. Please try again or contact support.';
}

/**
 * Process API response and handle errors consistently
 */
export async function processApiResponse<T>(response: Response, context?: Record<string, any>): Promise<T> {
  if (!response.ok) {
    let errorData = {};
    
    try {
      errorData = await response.json();
    } catch (e) {
      // If JSON parsing fails, continue with empty error data
    }
    
    throw new AirtableApiError(response.status, response.statusText, errorData, context);
  }
  
  try {
    return await response.json() as T;
  } catch (error) {
    throw new AirtableError(
      'Error parsing Airtable API response',
      AirtableErrorType.PARSING,
      ErrorSeverity.ERROR,
      error,
      context
    );
  }
} 
